// cspell:disable

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Modal, Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';
import blogFetch from '../../axios/config';
import { ModalSaidaEtiquetaInspFinalProps, StatusEtiquetaInspFinal } from './Types';

const ROTA_ETIQUETA_LIBERADA = 'Quality/etiqueta-liberada';
const ETIQUETA_IF = 'BR_Etiqueta_IF';

const MOTIVOS_POR_RESULTADO: Record<string, string> = {
    ORDEM_NAO_ENCONTRADA: 'Ordem não encontrada no sistema. Confira o número lido na ficha.',
    ERRO: 'Falha de comunicação com o servidor. Tente ler a ordem novamente.',
};

// Explica em texto o porquê da etiqueta não sair
const motivoBloqueio = (s: StatusEtiquetaInspFinal): string => {
    if (MOTIVOS_POR_RESULTADO[s.resultado]) return MOTIVOS_POR_RESULTADO[s.resultado];
    if (!s.apontado16ACV) return 'A peça não foi apontada no 16ACV (Autoclave) — sem esse apontamento a etiqueta não pode ser impressa.';
    if (!s.no18INSFIN)
        return `A peça não está no 18INSFIN${s.setorAtual ? ` — o setor atual é ${s.setorAtual}` : ''}.`;
    return s.motivo || 'Nenhum apontamento válido foi encontrado para esta ordem.';
};

const lerStatus = (data: any, ordemLida: string): StatusEtiquetaInspFinal => ({
    liberado: Boolean(data?.liberado),
    apontado16ACV: Boolean(data?.apontado16ACV),
    no18INSFIN: Boolean(data?.no18INSFIN),
    resultado: String(data?.resultado ?? ''),
    motivo: data?.motivo ?? data?.mensagem ?? data?.message ?? '',
    ordem: String(data?.ordem ?? ordemLida),
    lote: String(data?.lote ?? ''),
    setorAtual: String(data?.setorAtual ?? ''),
    ultimoSetorApontado: String(data?.ultimoSetorApontado ?? ''),
    ultimoStatusApontado: String(data?.ultimoStatusApontado ?? ''),
});

const ModalSaidaEtiquetaInspFinal: React.FC<ModalSaidaEtiquetaInspFinalProps> = ({ show, onHide }) => {

    const [ordem, setOrdem] = useState<string>("");
    const [status, setStatus] = useState<StatusEtiquetaInspFinal | null>(null);
    const [consultando, setConsultando] = useState<boolean>(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const processandoRef = useRef<boolean>(false);

    const consultarLiberacao = useCallback(async (ordemAlvo: string): Promise<StatusEtiquetaInspFinal | null> => {
        const ordemLimpa = (ordemAlvo || "").trim();
        if (!ordemLimpa) return null;

        setConsultando(true);
        try {
            const { data } = await blogFetch.get(`${ROTA_ETIQUETA_LIBERADA}/${encodeURIComponent(ordemLimpa)}`);
            const resultado = lerStatus(data, ordemLimpa);
            setStatus(resultado);
            return resultado;
        } catch (error: any) {
            const recebido = lerStatus(error?.response?.data, ordemLimpa);
            const naoEncontrada = error?.response?.status === 404;
            const resultado: StatusEtiquetaInspFinal = {
                ...recebido,
                liberado: false,
                resultado: recebido.resultado || (naoEncontrada ? 'ORDEM_NAO_ENCONTRADA' : 'ERRO'),
                motivo: recebido.motivo || (naoEncontrada
                    ? "Ordem não encontrada."
                    : "Não foi possível confirmar o apontamento no 16ACV."),
            };
            setStatus(resultado);
            return resultado;
        } finally {
            setConsultando(false);
        }
    }, []);

    const prepararProximaLeitura = () => {
        setOrdem("");
        inputRef.current?.focus();
    };

    const processarOrdem = async (ordemAlvo: string) => {
        const ordemLimpa = (ordemAlvo || "").trim();
        if (!ordemLimpa || processandoRef.current) return;

        processandoRef.current = true;
        try {
            const resultado = await consultarLiberacao(ordemLimpa);
            if (!resultado) return;

            if (!resultado.liberado) {
                toast.error(`Etiqueta NÃO impressa — ${motivoBloqueio(resultado)}`, { duration: 6000 });
                prepararProximaLeitura();
                return;
            }

            const ordemImpressa = resultado.ordem || ordemLimpa;
            window.location.href = `sagaprintserver:${ETIQUETA_IF};SAGA_User;${ordemImpressa}`;
            toast.success(`Etiqueta liberada para a ordem ${ordemImpressa}`);
            prepararProximaLeitura();
        } finally {
            processandoRef.current = false;
        }
    };

    const resetForm = () => {
        setOrdem("");
        setStatus(null);
    };

    const handleCancel = () => {
        resetForm();
        onHide();
    };

    const handleOrdemKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        const ordemAtual = ordem.trim();
        if (!ordemAtual) {
            toast.error("Informe a ordem da peça.");
            return;
        }
        processarOrdem(ordemAtual);
    };

    useEffect(() => {
        if (!show || consultando || ordem.trim().length < 8) return;
        const ordemAtual = ordem.trim();
        const timeout = setTimeout(() => {
            processarOrdem(ordemAtual);
        }, 800);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ordem, show]);

    useEffect(() => {
        if (show) {
            inputRef.current?.focus();
            return;
        }
        resetForm();
    }, [show]);

    const detalhes = status
        ? [
            { chave: "Ordem", valor: status.ordem },
            { chave: "Lote", valor: status.lote },
            {
                chave: "Apontamento 16ACV",
                valor: status.apontado16ACV ? "apontado" : "NÃO apontado",
                ok: status.apontado16ACV,
            },
            { chave: "Setor atual", valor: status.setorAtual, ok: status.no18INSFIN },
            { chave: "Último setor apontado", valor: status.ultimoSetorApontado },
            { chave: "Status do apontamento", valor: status.ultimoStatusApontado },
        ].filter(({ valor }) => !!valor)
        : [];

    return (
        <Modal show={show} onHide={handleCancel} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Saída da Etiqueta — Inspeção Final</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="card p-3 shadow-sm rounded-3">
                    <Form onSubmit={(e: any) => e.preventDefault()}>
                        <Form.Group className="mb-3">
                            <Form.Label>Ordem</Form.Label>
                            <Form.Control
                                type="text"
                                size="lg"
                                value={ordem}
                                ref={inputRef}
                                autoFocus
                                autoComplete="off"
                                onChange={(e) => setOrdem(e.target.value)}
                                onKeyDown={handleOrdemKeyDown}
                                placeholder="Bipe ou digite a ordem"
                            />
                        </Form.Group>
                    </Form>

                    {consultando && (
                        <div className="text-center my-2">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Consultando apontamento no 16ACV...
                        </div>
                    )}

                    {status && !consultando && (
                        <div
                            className={`border rounded p-3 ${status.liberado
                                ? 'border-success bg-success bg-opacity-10'
                                : 'border-danger bg-danger bg-opacity-10'}`}
                        >
                            <div
                                className={`fw-bold text-center mb-3 ${status.liberado ? 'text-success' : 'text-danger'}`}
                                style={{ fontSize: '1.6rem' }}
                            >
                                {status.liberado ? '✓ ETIQUETA LIBERADA' : '✗ ETIQUETA NÃO IMPRESSA'}
                            </div>

                            {!status.liberado && (
                                <div className="bg-danger text-white rounded p-3 mb-3 text-center">
                                    <div className="fw-bold text-uppercase" style={{ letterSpacing: '.05rem' }}>
                                        Motivo
                                    </div>
                                    <div className="fw-semibold fs-5 mt-1">{motivoBloqueio(status)}</div>
                                    {status.motivo && status.motivo !== motivoBloqueio(status) && (
                                        <div className="mt-2 small">{status.motivo}</div>
                                    )}
                                </div>
                            )}

                            {detalhes.length > 0 && (
                                <ul className="list-unstyled mb-0">
                                    {detalhes.map(({ chave, valor, ok }) => (
                                        <li key={chave} className="d-flex justify-content-between border-top py-2 fs-5">
                                            <span className="text-muted">{chave}</span>
                                            <span className={`fw-bold text-end ${ok === undefined ? '' : ok ? 'text-success' : 'text-danger'}`}>
                                                {valor}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <button className="btn btn-outline-secondary px-4" onClick={handleCancel}>Fechar</button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalSaidaEtiquetaInspFinal;
